import { FC, memo, useMemo } from 'react'

import styled, { useTheme } from 'styled-components'
import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import { useAppSelector } from 'src/Hooks/redux'
import {
  getEstimatedTiktokVideos,
  getEstimatedTiktokViews,
  getIsTiktokAudienceLoading,
} from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { PrimaryHint } from 'src/Components/PrimaryHint'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { TikTokCampaignPricingModelEnum } from 'src/Types/campaigns'

const Container = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  min-height: 86px;
`
const TitleContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const Value = styled(Title)`
  font-size: 18px;
  font-weight: 600;
`

const StyledSpin = styled(Spin)`
  margin: auto auto;
`

export const TiktokScheduleBudgetEstimation: FC = memo(() => {
  const theme = useTheme()
  const loading = useAppSelector(getIsTiktokAudienceLoading)
  const { maxViews, minViews } = useAppSelector(getEstimatedTiktokViews)
  const { estimatedMinVideos, estimatedMaxVideos } = useAppSelector(
    getEstimatedTiktokVideos,
  )
  const { pricingModel } = useAppSelector(getScheduledTikTokCampaign)

  const isVideosBased =
    pricingModel === TikTokCampaignPricingModelEnum.VideosBased

  const viewsValue = useMemo(() => {
    if (!minViews && !maxViews) {
      return '-'
    }
    if (minViews === maxViews) {
      return `~${minViews}`
    }
    return `${minViews} - ${maxViews}`
  }, [maxViews, minViews])

  if (loading) {
    return (
      <Container>
        <StyledSpin
          style={{ color: theme.color.brandGreen }}
          indicator={<LoadingOutlined spin />}
          size='large'
        />
      </Container>
    )
  }

  if (isVideosBased) {
    return (
      <Container>
        <TitleContainer>
          <Title>Potential videos</Title>
        </TitleContainer>
        <Value>
          {estimatedMinVideos} - {estimatedMaxVideos}
        </Value>
      </Container>
    )
  }

  return (
    <Container>
      <TitleContainer>
        <Title>Potential views</Title>
        <PrimaryHint>
          The range of potential views you will receive with the selected budget
        </PrimaryHint>
      </TitleContainer>
      <Value>{viewsValue}</Value>
    </Container>
  )
})
